import { FetchResult, MutationFunctionOptions } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDKeybind from '../../../../../../BaseComponents/Keybinds/EDKeybind';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import { TEDSYSUserPwdResponse } from '../../../../../../globalQueryTypes';
import { findFormatter } from '../../../../../../Utils/Formatters/FindFormatter';
import InfoArea from '../../../InfoArea/InfoArea';
import { ResetPwdDialog } from '../../../LoginForm/Components/ResetPwdLink/Components/ResetPwdDialog/ResetPwdDialog';
import {
  ChangePwd,
  ChangePwdVariables,
} from '../../Mutations/_QueryTypes/ChangePwd';

interface IProps {
  isChangingPassword: boolean;
  errorMessage: string | undefined;
  result?: TEDSYSUserPwdResponse | null;
  dbName: string | null;
  newPwdKey: string | null;
  changePwdMutation?: (
    options?: MutationFunctionOptions<ChangePwd, ChangePwdVariables>
  ) => Promise<FetchResult<ChangePwd>>;
  data: ChangePwd | undefined | null;
  validTo?: string | null;
}

export const NewPasswordForm: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { changePwdMutation } = props;
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordConfirmTouched, setPasswordConfirmTouched] = useState(false);
  const [resetPwdDialogOpen, setResetPwdDialogOpen] = useState(false);
  const parsedValidTo = findFormatter('TEDFormatDateTime').parser.parse(
    props.validTo ?? ''
  ).value;

  const keyExpireTime =
    typeof parsedValidTo === 'string'
      ? new Date(parsedValidTo).getTime()
      : new Date().getTime();
  const [msToKeyExpire, setMsToKeyExpire] = useState(
    keyExpireTime - new Date().getTime()
  );
  const minutesToKeyExpire = Math.max(
    Math.ceil((msToKeyExpire % (1000 * 60 * 60)) / (1000 * 60)),
    0
  );
  const keyHasExpired = msToKeyExpire <= 0;

  useEffect(() => {
    const interval = setInterval(() => {
      setMsToKeyExpire(keyExpireTime - new Date().getTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [keyExpireTime]);

  const navigate = useNavigate();

  const passwordsAreEqual = password === passwordConfirm;
  const validPassword = password && passwordsAreEqual;

  const showPasswordConfirmError =
    !passwordsAreEqual && passwordConfirm && passwordConfirmTouched;

  const changePwd = useCallback(() => {
    if (props.dbName && props.newPwdKey && changePwdMutation) {
      changePwdMutation({
        variables: { dbname: props.dbName, password, key: props.newPwdKey },
      });
    } else {
      throw new Error(
        `Failed to send changePwdQuery, dbname: ${props.dbName} key: ${props.newPwdKey} changePwdMutation: ${changePwdMutation}`
      );
    }
  }, [password, changePwdMutation, props.dbName, props.newPwdKey]);

  if (
    (!props.data ||
      props.result === TEDSYSUserPwdResponse.TRY_AGAIN ||
      props.result === TEDSYSUserPwdResponse.ERROR) &&
    !keyHasExpired
  ) {
    const changePwdDisabled = !validPassword || props.isChangingPassword;
    return (
      <EDKeybind
        keymap="Enter"
        onKeyDown={() => {
          if (!changePwdDisabled) {
            changePwd();
          }
        }}
      >
        {!keyHasExpired && (
          <EDTypography>
            Denna länk är giltig i ytterligare {minutesToKeyExpire}{' '}
            {minutesToKeyExpire === 1 ? 'minut' : 'minuter'}
          </EDTypography>
        )}
        <EDTextEdit
          label="Nytt lösenord"
          value={password}
          onChange={setPassword}
          type={'password'}
          testId="newPasswordEdit"
        />
        <EDTextEdit
          label="Bekräfta det nya lösenordet"
          value={passwordConfirm}
          onChange={setPasswordConfirm}
          type={'password'}
          helperText={
            showPasswordConfirmError ? 'Lösenorden matchar inte' : undefined
          }
          error={Boolean(showPasswordConfirmError)}
          onBlur={() => {
            setPasswordConfirmTouched(true);
          }}
          testId="confirmNewPasswordEdit"
        />
        <InfoArea type="error">{props.errorMessage} </InfoArea>
        <EDButton
          label={
            props.isChangingPassword ? 'Ändrar lösenord' : 'Ändra lösenord'
          }
          disabled={changePwdDisabled}
          onClick={changePwd}
          primary
          fullWidth
          marginY={2}
          testId="changePasswordButton"
        />
      </EDKeybind>
    );
  } else if (props.result === TEDSYSUserPwdResponse.OK) {
    return (
      <>
        <EDTypography>
          Lösenordet har blivit ändrat. Du kan nu logga in med ditt nya lösenord
        </EDTypography>
        <EDButton
          label={'Logga in'}
          onClick={() => {
            navigate('/login');
          }}
          primary
          fullWidth
          marginY={2}
        />
      </>
    );
  } else if (keyHasExpired) {
    return (
      <>
        {keyHasExpired && (
          <EDTypography>Denna länk är inte längre giltig</EDTypography>
        )}
        <EDButton
          label={'Skicka en ny länk'}
          onClick={() => {
            setResetPwdDialogOpen(true);
          }}
          primary
          fullWidth
          marginY={2}
        />
        <ResetPwdDialog
          open={resetPwdDialogOpen}
          setOpen={setResetPwdDialogOpen}
          dbname={props.dbName ?? undefined}
        />
      </>
    );
  } else {
    return (
      <InternalError id="NewPasswordFormUnknownError" message="Okänt fel" />
    );
  }
};
